import React, { FC, useCallback, useContext, useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { AcornsIcon } from '@acorns/icons'
import { Spinner } from '@acorns/web-components'
import { getImageAsset, useRemoteStrings } from '@acorns/web-utils'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { compose } from 'recompose'

import { USER_UUID } from 'app-constants'
import Button from 'components/Button'
import CenteredSpinner from 'components/CenteredSpinner'
import withViewportMeta, { State as ScrollProps } from 'components/HOCs/WithViewportMeta'
import Nav from 'components/Nav'
import { ActionType, PageName, TierGroup } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import theme from 'theme'
import trackEvent from 'utils/analytics/trackEvent'
import { withApolloErrorModal } from 'utils/Apollo'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'
import {
  setTierKey,
  setTierName,
  setTierPrice,
  setTierPriceIdCookie,
} from 'utils/TieredRegistration/utils'

import {
  getBackButtonTappedEventData,
  getChangeTierButtonTappedEventData,
  getConfirmButtonTappedEventData,
  getScreenViewEventData,
} from './eventData'
import subscribeToTierMutation from './gql/SubscribeToTier.mutation.gql'
import tierGroupForUserQuery from './gql/TierGroupForUser.query.gql'
import {
  BenefitsContainer,
  BundleContainer,
  BundleHeader,
  BundleName,
  BundleProductContainer,
  BundleProductDescription,
  ButtonContainer,
  Divider,
  Header,
  HeaderContent,
  Name,
  PillNavContainer,
  Price,
  StyledIcon,
  StyledLayout,
  StyledPillNavigation,
  TierDescription,
  TierOption,
} from './styled-components'
import { formatTierPrice, hasProductInTier } from './utils'

type Props = EnhancedProps
type EnhancedProps = WithFlowRouterProps & ScrollProps

const enhance = compose<Props, EnhancedProps>(
  withApolloErrorModal,
  withFlowRouter,
  withViewportMeta,
)

const Component: FC<Props> = props => {
  const isDesktop = useMediaQuery({ minWidth: `${theme.breakpoints.sm + 1}px` })
  const { isBackEnabled, isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [containerHeight, setContainerHeight] = useState<number>()
  const [hasScrolled, setHasScrolled] = useState<boolean>(false)
  const { getRemoteString } = useRemoteStrings()

  const navigationRef = useCallback(node => {
    if (node) {
      setContainerHeight(node.clientHeight)
    }
  }, [])

  const handleScroll = e => {
    setHasScrolled(e.target.scrollTop)
  }

  useEffect(() => {
    isDesktop && setHasScrolled(props.scrollTop > 0)
  }, [isDesktop, props.scrollTop])

  const { data: tierGroupData, loading: isTierGroupDataLoading } = useQuery<{
    tierGroupForUser: TierGroup
  }>(tierGroupForUserQuery, { fetchPolicy: 'network-only' })
  const [subscribeToTier, { loading: isSubscribeToTierLoading }] = useMutation(
    subscribeToTierMutation,
  )

  // Sort tiers in descending order by price
  const sortedTiers = tierGroupData?.tierGroupForUser.tierOptions.sort(
    (tier1, tier2) => tier2.preferredTierPrice.amount.value - tier1.preferredTierPrice.amount.value,
  )

  const updateTierInStorage = (index?: number) => {
    setTierName(sortedTiers[index ?? selectedIndex].tier.name)
    setTierPriceIdCookie(sortedTiers[index ?? selectedIndex].tier.tierPrice.id)
    setTierPrice(sortedTiers?.[index ?? selectedIndex].preferredTierPrice.amount.value)
    setTierKey(sortedTiers?.[index ?? selectedIndex].tier.key)
  }

  const options = sortedTiers?.map(({ tier }, i) => ({
    id: i,
    text: tier.displayName ?? tier.name,
    handler: () => {
      setSelectedIndex(i)
      updateTierInStorage(i)

      const trackData = {
        funnel: props.flow,
        tierKey: sortedTiers?.[i].tier.key,
        tierPrice: sortedTiers?.[i].preferredTierPrice.amount.value,
      }
      trackEvent('Button Tapped', {
        eventData: getChangeTierButtonTappedEventData(trackData),
      })
    },
  }))

  const handleCtaPress = async () => {
    updateTierInStorage()

    trackEvent('Button Tapped', {
      eventData: getConfirmButtonTappedEventData({
        funnel: props.flow,
        tierKey: sortedTiers?.[selectedIndex].tier.key,
        tierPrice: sortedTiers?.[selectedIndex].preferredTierPrice.amount.value,
      }),
    })

    await subscribeToTier({
      variables: {
        input: {
          tierPriceId: sortedTiers[selectedIndex].tier.tierPrice.id,
          tierId: sortedTiers[selectedIndex].tier.id,
          userId: window.sessionStorage.getItem(USER_UUID),
        },
      },
    })

    isDynamicFunnelEnabled
      ? routeToPage(PageName.PricingInterstitial, ActionType.Continue)
      : props.route({ from: FlowRoute.PricingInterstitial, action: 'continue' })
  }

  useEffect(() => {
    if (sortedTiers?.length) {
      trackEvent('Screen Viewed', {
        eventData: getScreenViewEventData({
          funnel: props.flow,
          tierKey: sortedTiers?.[selectedIndex].tier.key,
          tierPrice: sortedTiers?.[selectedIndex].preferredTierPrice.amount.value,
        }),
      })
    }
  }, [sortedTiers, selectedIndex, props.flow])

  useEffect(() => {
    if (sortedTiers) {
      setSelectedIndex(
        sortedTiers.findIndex(
          option => option.tier.id === tierGroupData.tierGroupForUser.preferredTierId,
        ),
      )
    }
  }, [sortedTiers])

  if (isTierGroupDataLoading) {
    return <CenteredSpinner />
  }

  return (
    <>
      <Nav
        back={isDynamicFunnelEnabled && isBackEnabled(PageName.PricingInterstitial)}
        headerText={getRemoteString({ key: 'registration.pricing.page_title' })}
        hideBorder={true}
        noShim={!isDesktop}
        backClickEventDataOverride={getBackButtonTappedEventData({
          funnel: props.flow,
          tierKey: sortedTiers?.[selectedIndex].tier.key,
          tierPrice: sortedTiers?.[selectedIndex].preferredTierPrice.amount.value,
        })}
      />
      <StyledLayout>
        <PillNavContainer ref={navigationRef} hasBorder={hasScrolled}>
          <StyledPillNavigation
            options={options}
            selectedIndex={selectedIndex}
            onSelect={() => null}
          />
        </PillNavContainer>
        {sortedTiers?.map((tierOption, tierOptionIndex) => (
          <TierOption
            key={`tier-option-${tierOptionIndex}`}
            hidden={selectedIndex !== tierOptionIndex}
            height={containerHeight}
            onScroll={handleScroll}
          >
            <Header bgImageUrl={getImageAsset(tierOption.tier.assetPath, 'svg').url}>
              <HeaderContent>
                <Name data-testid={TestIDs.PRICING_INTERSTITIAL.TIER_NAME}>
                  {tierOption.tier.name}
                </Name>
                <Price data-testid={TestIDs.PRICING_INTERSTITIAL.TIER_PRICE}>
                  {formatTierPrice(tierOption.preferredTierPrice)}
                </Price>
                <TierDescription>{tierOption.tier.description}</TierDescription>
              </HeaderContent>
            </Header>
            <Divider />
            <BenefitsContainer data-testid={TestIDs.PRICING_INTERSTITIAL.BENEFITS_CONTAINER}>
              {sortedTiers[selectedIndex].tier.bundles.map((bundle, bundleIndex) => (
                <BundleContainer
                  key={`bundle-container-${tierOptionIndex}-${bundleIndex}`}
                  data-testid={TestIDs.PRICING_INTERSTITIAL.BUNDLE_CONTAINER}
                >
                  <BundleHeader>
                    <img src={getImageAsset(bundle.iconPath, 'svg').url} alt={'Product grouping'} />
                    <BundleName>{bundle.name}</BundleName>
                  </BundleHeader>
                  {bundle.products.map((bundleProduct, bundleProductIndex) => {
                    return bundleProduct.descriptionKeys.map(
                      (descriptionKey, descriptionKeyIndex) => {
                        const isProductInTier = hasProductInTier(
                          bundleProduct.product.key,
                          tierOption.tier,
                        )
                        return (
                          <BundleProductContainer
                            key={`product-description-${tierOptionIndex}-${bundleProductIndex}-${descriptionKeyIndex}`}
                          >
                            <StyledIcon
                              icon={
                                isProductInTier
                                  ? AcornsIcon.Icon.CheckmarkFv
                                  : AcornsIcon.Icon.InlineClose
                              }
                              width={isProductInTier ? 14 : 12}
                              height={isProductInTier ? 14 : 12}
                              isEnabled={isProductInTier}
                            />
                            <BundleProductDescription isEnabled={isProductInTier}>
                              {getRemoteString({ key: descriptionKey })}
                            </BundleProductDescription>
                          </BundleProductContainer>
                        )
                      },
                    )
                  })}
                </BundleContainer>
              ))}
            </BenefitsContainer>
          </TierOption>
        ))}
        <ButtonContainer>
          <Button
            onPress={handleCtaPress}
            disabled={isSubscribeToTierLoading}
            data-testid={TestIDs.PRICING_INTERSTITIAL.CTA}
          >
            {isSubscribeToTierLoading ? (
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore This component belongs to web-core. Ignoring JSX component type error until this
              // is fixed in web-core
              <Spinner height={20} width={20} white={true} />
            ) : (
              getRemoteString({ key: 'registration.pricing.cta' })
            )}
          </Button>
        </ButtonContainer>
      </StyledLayout>
    </>
  )
}

export const DynamicPricingInterstitial = enhance(Component)
